import { useApolloClient } from "@apollo/client";
import { useRef, useEffect, useState, useMemo, useCallback } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import cuid from "cuid";
import { get } from "lodash";
import omitDeep from "omit-deep-lodash";
import { fetchZipCodeCities } from "shared/hooks/useZipCodeCities";
import { NAME_SPACES } from "shared/locales/constants";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import {
  DOWNLOAD_DOCUMENT,
  DOWNLOAD_SINGLE_DOCUMENT,
} from "utils/api/graphql/mutations/download-document";
import {
  ATTACHMENT_TYPES,
  PROCESS_TRIGGER_NAMES,
  SCREEN_BINDING_KINDS,
} from "utils/constants";
import { downloadDocument, downloadFiles } from "utils/helpers/files";
import { toBooleanFieldValue } from "utils/helpers/booleanField";
import { isProjectRia, resolvePlanDateDeltas } from "utils/helpers/array";
import { usesPlanDeltas } from "utils/validation-rules";
import { CollectionItem } from "./CollectionItem";
import { bindAdvisorCodeEvents } from "./advisorCode";
import { bindExecuteFieldEvents } from "./executeField";
import { applyHopBusy, resolveHopOrigin } from "./hopBusy";
import {
  applyBlockedState,
  applyLockedState,
  collectMissingRequired,
} from "./blockedState";
import {
  buildStateContext,
  resolveActiveState,
  resolveSlots,
  storageKeyOf,
} from "./conditions";
import { findFormField, mergeFieldDefinition, setupHydration } from "./hydrate";
import { createPendingWriteQueue } from "./pendingWrites";
import { useAttachmentField } from "./useAttachmentField";
import { useCompanySearch } from "./useCompanySearch";
import { useFieldRegistry } from "./useFieldRegistry";
import { applyCollectionFieldChange } from "./collectionMutations";
import { toEntityWriteValue } from "./entityWriteValue";
import { collectPrefetchPlanIds, selectedPlanOptionIds } from "./factorPrices";
import { priceScopeKey } from "./priceCacheScope";
import {
  readDocumentScroll,
  shouldKeepIframeScroll,
  writeDocumentScroll,
} from "./iframeScroll";
import { nextScreenVisit } from "./screenVisit";
import { catalogFromStatusData } from "./dataSources";
import { useFactorPrices } from "./useFactorPrices";
import { useInsurancePlans } from "./useInsurancePlans";
import { usePlanAttachments } from "./usePlanAttachments";
import { usePlanPrices } from "./usePlanPrices";
import { useComputedFields } from "./useComputedFields";
import { applyPriceSlotLoading, isVisibleComputeBusy } from "./computeBusy";
import { applyProgressBar, parseProgressPercent } from "./progressBar";
import {
  areCollectionDownloadsComplete,
  collectionDownloadQuery,
  collectionNamesToHydrate,
  collectionZipName,
  projectWithLocalCollections,
  extractCollections,
  resolveHydratedCollectionItems,
  findCollectionListSlot,
  isPlanScopedList,
  injectViewportMeta,
  SCREEN_IFRAME_SANDBOX,
  SCREEN_IFRAME_SLOT_CHROME,
  SCREEN_IFRAME_VIEWPORT_META,
  listAttachmentTypes,
  mergeDownloadedIds,
  parseDownloadedIds,
  resolveCollectionItems,
  resolveDownloadCollectionItems,
  collectEmptyDownloadCollectionSlotIds,
  resolveDownloadTracking,
  shouldZipCollectionDownload,
  resolveSelectedPlanId,
  toDocumentCollectionItems,
} from "./utils";

const buildFullHtml = (htmlContent, styleContent) => {
  if (!htmlContent) return "";
  const isFullDocument =
    htmlContent.indexOf("<!DOCTYPE") >= 0 || htmlContent.indexOf("<html") >= 0;
  const sharedStyle = styleContent ? `<style>${styleContent}</style>` : "";
  if (isFullDocument) {
    return injectViewportMeta(
      htmlContent.replace(
        "</head>",
        sharedStyle + SCREEN_IFRAME_SLOT_CHROME + "</head>",
      ),
    );
  }
  return (
    "<!DOCTYPE html><html><head>" +
    SCREEN_IFRAME_VIEWPORT_META +
    "<style>body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }</style>" +
    sharedStyle +
    SCREEN_IFRAME_SLOT_CHROME +
    "</head><body>" +
    htmlContent +
    "</body></html>"
  );
};

const COLLECTION_ITEM_ACTIONS = {
  DOWNLOAD: "download",
  SELECT_PLAN: "selectPlan",
  SEND_QUOTE: "sendQuote",
  SEND_QUOTE_AND_SUBSCRIBE: "sendQuoteAndSubscribe",
};

const QUOTE_ITEM_ACTIONS = [
  COLLECTION_ITEM_ACTIONS.SEND_QUOTE,
  COLLECTION_ITEM_ACTIONS.SEND_QUOTE_AND_SUBSCRIBE,
];

const PLAN_DOCUMENT_ACTIONS = {
  downloadExample: ATTACHMENT_TYPES.REIMBURSEMENT_EXAMPLES,
  downloadGuarantees: ATTACHMENT_TYPES.BROCHURE,
  downloadIpid: ATTACHMENT_TYPES.IPID,
};

const ScreenRenderer = ({
  version,
  project = {},
  data = {},
  formDefinition,
  onAction,
  busy = false,
  processId,
  progress,
}) => {
  const { t: tErrors } = useTranslation(NAME_SPACES.ERRORS);
  const validationMessages = tErrors("VALIDATIONS", { returnObjects: true });
  const validationMessagesRef = useRef(validationMessages);
  const iframeRef = useRef(null);
  const lastVersionIdRef = useRef(null);
  const debounceTimers = useRef({});
  const writeQueueRef = useRef(createPendingWriteQueue());
  const conditionalEvaluator = useRef(null);
  const hydrateRef = useRef(null);

  const projectRef = useRef(project);
  const onActionRef = useRef(onAction);
  const handleFieldChangeRef = useRef(null);
  const onAddCollectionItemRef = useRef(null);
  const slotsRef = useRef([]);
  const formDefinitionRef = useRef(formDefinition);
  const dateDeltasRef = useRef({});
  const collectionDefaultsRef = useRef({});
  const downloadedIdsRef = useRef([]);
  const trackedProjectIdRef = useRef(null);
  const processIdRef = useRef(processId);
  processIdRef.current = processId;
  const resolveAdvisorCodeRef = useRef(null);

  const [updateEntity] = useMutation(graphql.mutations.UPDATE_ENTITY);
  const [executeField] = useMutation(graphql.queries.EXECUTE_FIELD);
  const [resolveAdvisorCode] = useLazyQuery(
    graphql.queries.RESOLVE_PROCESS_ADVISOR_CODE,
    { onError: () => {} },
  );
  resolveAdvisorCodeRef.current = resolveAdvisorCode;
  const [downloadSingleDocument] = useMutation(DOWNLOAD_SINGLE_DOCUMENT);
  const [downloadDocuments] = useMutation(DOWNLOAD_DOCUMENT);
  const mountAttachmentField = useAttachmentField();
  const apolloClient = useApolloClient();
  const fetchCities = useCallback(
    (zipCode) => fetchZipCodeCities(apolloClient, zipCode),
    [apolloClient],
  );

  const [pendingFields, setPendingFields] = useState({});

  const stateContext = useMemo(
    () => buildStateContext(project, pendingFields),
    [project, pendingFields],
  );

  const bufferedProject = useMemo(
    () => ({
      ...stateContext,
      fields: stateContext.fields,
    }),
    [stateContext],
  );

  const activeState = resolveActiveState(version?.states, stateContext);

  const htmlContent = activeState
    ? activeState.htmlContent
    : version?.htmlContent;
  const manifest = activeState
    ? activeState.bindingManifest
    : version?.bindingManifest;
  const slots = useMemo(
    () => resolveSlots(manifest, version?.states, htmlContent),
    [manifest, version?.states, htmlContent],
  );

  const screenVisitRef = useRef(null);
  screenVisitRef.current = nextScreenVisit(
    screenVisitRef.current,
    version?.id || "",
  );
  const screenVisit = screenVisitRef.current.visit;
  const priceScope = useMemo(
    () => priceScopeKey({ screenVisit, project: bufferedProject }),
    [screenVisit, bufferedProject],
  );

  const { registry, registryRef } = useFieldRegistry(processId);
  const extracted = useMemo(
    () => extractCollections(htmlContent, { slots }),
    [htmlContent, slots],
  );

  const [collections, setCollections] = useState({});
  const collectionsRef = useRef({});
  collectionsRef.current = collections;
  const [portalAnchors, setPortalAnchors] = useState({});

  const hasPlansCollection = slots.some(
    (s) => s.type === "list" && s.binding && s.binding.source === "plans",
  );
  const hasPlanDeltaField = useMemo(() => {
    const formFields = get(formDefinition, "fields") || [];
    if (formFields.some((field) => usesPlanDeltas(field.inputConfig))) {
      return true;
    }
    return slots.some((slot) => {
      if (slot.type !== "input") return false;
      if (!slot.binding || slot.binding.kind !== "entity_field") {
        return false;
      }
      const findInEntity = (entityKey) => {
        const group = (registry || []).find((item) => item.key === entityKey);
        return group
          ? group.fields.find((item) => item.key === slot.binding.field) || null
          : null;
      };
      const registryField =
        findInEntity(slot.binding.entity) ||
        (slot.binding.entity === "advanced" ? findInEntity("project") : null);
      const formField = findFormField(
        formDefinition,
        slot.binding.entity,
        slot.binding.field,
      );
      const fieldDef = mergeFieldDefinition(registryField, formField);
      return usesPlanDeltas(fieldDef && fieldDef.inputConfig);
    });
  }, [formDefinition, slots, registry]);
  const selectedToCompareKey = (
    (project && project.fields && project.fields.selectedToCompare) ||
    []
  )
    .filter(Boolean)
    .join(",");
  const selectedToCompare = useMemo(
    () => selectedToCompareKey.split(",").filter(Boolean),
    [selectedToCompareKey],
  );
  const { insurancePlans } = useInsurancePlans(
    hasPlansCollection || hasPlanDeltaField,
    selectedToCompare,
  );
  const planAttachmentSlot = useMemo(
    () => (slots || []).find((slot) => isPlanScopedList(slot)),
    [slots],
  );
  const attachmentPlanId = resolveSelectedPlanId(bufferedProject);
  const { planAttachments } = usePlanAttachments({
    enabled: !!planAttachmentSlot,
    planId: attachmentPlanId,
    types: listAttachmentTypes(planAttachmentSlot),
    projectId: project && project.id,
  });
  const statusAttachments = useMemo(
    () => catalogFromStatusData(data).attachments,
    [data],
  );
  const planAttachmentsCount = planAttachments.length;
  const dateDeltas = useMemo(() => {
    if (!hasPlanDeltaField) return {};
    return resolvePlanDateDeltas({
      insurancePlans,
      ria: isProjectRia(bufferedProject),
    });
  }, [hasPlanDeltaField, insurancePlans, bufferedProject]);
  const { planPrices } = usePlanPrices(project, insurancePlans);
  const planPricesCount = Object.keys(planPrices).length;
  const selectedPlanId = bufferedProject?.fields?.selectedPlan;
  const { computed: computedFields } = useComputedFields(
    bufferedProject,
    registry,
    priceScope,
  );
  const [hydrationKey, setHydrationKey] = useState(0);
  const [prefetchPlanIds, setPrefetchPlanIds] = useState([]);
  useEffect(() => {
    const doc = iframeRef.current && iframeRef.current.contentDocument;
    setPrefetchPlanIds(
      collectPrefetchPlanIds({
        selectedPlanId,
        extraIds: [
          ...selectedPlanOptionIds(doc, slots),
          ...(selectedToCompare || []).filter(Boolean),
        ],
      }),
    );
  }, [hydrationKey, slots, selectedPlanId, selectedToCompare]);
  const { factorPrices, computeState: factorPricesState } = useFactorPrices(
    bufferedProject,
    slots,
    prefetchPlanIds,
    priceScope,
  );
  const { setQuery: setCompanyQuery, results: companySearchResults } =
    useCompanySearch();

  const hasHydrated = useRef(false);
  const hopOriginKeyRef = useRef(null);
  const screenKey = version && version.id;

  useEffect(() => {
    const resolved = resolveHopOrigin(busy, screenKey, hopOriginKeyRef.current);
    hopOriginKeyRef.current = resolved.originKey;
    applyHopBusy(
      iframeRef.current && iframeRef.current.contentDocument,
      slots,
      resolved.busy,
      { inventLoader: false },
    );
  }, [busy, slots, screenKey]);

  useEffect(() => {
    applyPriceSlotLoading(
      iframeRef.current?.contentDocument,
      slots,
      isVisibleComputeBusy(factorPricesState),
    );
  }, [factorPricesState, slots, hydrationKey]);

  const progressPercent = useMemo(
    () => parseProgressPercent(progress),
    [progress],
  );

  useEffect(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc || progressPercent == null) return;
    applyProgressBar(doc, progressPercent);
  }, [progressPercent, hydrationKey]);

  const lookupFieldDefinition = useCallback(
    (entity, field) => {
      const findInEntity = (entityKey) => {
        const group = (registry || []).find((item) => item.key === entityKey);
        return group
          ? group.fields.find((item) => item.key === field) || null
          : null;
      };
      const registryField =
        findInEntity(entity) ||
        (entity === "advanced" ? findInEntity("project") : null);
      return mergeFieldDefinition(
        registryField,
        findFormField(formDefinition, entity, field),
      );
    },
    [registry, formDefinition],
  );

  const missingRequired = useMemo(
    () =>
      collectMissingRequired({
        formDefinition,
        slots,
        context: stateContext,
        project: bufferedProject,
        lookupFieldDefinition,
      }),
    [
      formDefinition,
      slots,
      stateContext,
      bufferedProject,
      lookupFieldDefinition,
    ],
  );

  const emptyDownloadSlotIds = useMemo(
    () =>
      collectEmptyDownloadCollectionSlotIds({
        slots,
        collections,
        project: bufferedProject,
        registry,
        statusAttachments,
      }),
    [slots, collections, bufferedProject, registry, statusAttachments],
  );

  useEffect(() => {
    applyBlockedState(
      iframeRef.current?.contentDocument,
      slotsRef.current,
      stateContext,
      missingRequired,
      { emptyDownloadSlotIds },
    );
    applyLockedState(
      iframeRef.current?.contentDocument,
      slotsRef.current,
      Boolean(project && project.locked),
    );
  }, [
    stateContext,
    missingRequired,
    hydrationKey,
    emptyDownloadSlotIds,
    project,
  ]);

  projectRef.current = {
    ...stateContext,
    // Resolved collection defaults are spread first so persisted fields and
    // pending edits always win over them. In-memory collections win last so
    // a state remount cannot drop items the parent project has not echoed.
    fields: {
      ...collectionDefaultsRef.current,
      ...stateContext.fields,
      ...projectWithLocalCollections(
        { fields: {} },
        collectionsRef.current,
        Object.keys(collectionsRef.current || {}),
      ).fields,
    },
    advanced: {
      ...(computedFields || {}),
      ...(factorPrices || {}),
      searchResults: companySearchResults || [],
    },
  };
  slotsRef.current = slots;
  formDefinitionRef.current = formDefinition;
  validationMessagesRef.current = validationMessages;
  dateDeltasRef.current = dateDeltas;

  useEffect(() => {
    if (!project) return;
    const projectWithAdvanced = {
      ...stateContext,
      fields: { ...stateContext.fields },
      advanced: {
        ...(computedFields || {}),
        ...(factorPrices || {}),
      },
    };
    const ctx = {
      project: projectWithAdvanced,
      insurancePlans,
      planPrices,
    };
    const collectionNames = collectionNamesToHydrate(
      slots,
      extracted.collections,
    );
    setCollections((prev) => {
      const next = { ...prev };
      const projectForHydrate = projectWithLocalCollections(
        projectWithAdvanced,
        prev,
        collectionNames,
      );
      const hydrateCtx = { ...ctx, project: projectForHydrate };
      for (const name of collectionNames) {
        const listSlot = findCollectionListSlot(slots, name);
        next[name] = resolveHydratedCollectionItems({
          name,
          project: projectForHydrate,
          registry,
          listSlot,
          planAttachments,
          statusAttachments,
          ctx: hydrateCtx,
        });
      }
      return next;
    });
    if (project.fields) {
      // Defaults are handed to the iframe handlers through projectRef instead
      // of being written into the project prop, which must stay immutable.
      const defaults = {};
      for (const name of collectionNames) {
        const entry = (registry || []).find((g) => g.key === name);
        const listSlot = findCollectionListSlot(slots, name);
        if (isPlanScopedList(listSlot)) {
          defaults[name] = toDocumentCollectionItems(planAttachments);
        } else if (
          entry &&
          entry.store &&
          entry.store.kind !== "advanced" &&
          !project.fields[name]
        ) {
          defaults[name] = resolveCollectionItems(projectWithAdvanced, entry);
        }
      }
      collectionDefaultsRef.current = defaults;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    project,
    extracted,
    slots,
    registry.length,
    insurancePlans.length,
    planPricesCount,
    planAttachmentsCount,
    statusAttachments.length,
    computedFields,
  ]);

  const sendCollectionMutation = useCallback(
    (name, items, immediate) => {
      const pd = projectRef.current;
      if (!pd || !pd.id) return;
      if (!pd.fields) pd.fields = {};
      pd.fields[name] = items;
      const key = "collection:" + name;
      const fire = () =>
        updateEntity({
          variables: {
            project: { id: pd.id },
            entity: "collection",
            field: name,
            value: items,
          },
        });
      writeQueueRef.current.enqueue(key, fire, immediate ? 0 : 500);
    },
    [updateEntity],
  );

  // The ref is refreshed on render, so two edits landing in the same tick
  // (autofill, paste, fast typing across two fields) would both start from the
  // same items and the second one would drop the first field.
  const commitCollection = useCallback((name, items) => {
    collectionsRef.current = { ...collectionsRef.current, [name]: items };
    setCollections((prev) => ({ ...prev, [name]: items }));
  }, []);

  const handleAddCollectionItem = useCallback(
    (name) => {
      const items = [...(collectionsRef.current[name] || []), { id: cuid() }];
      commitCollection(name, items);
      sendCollectionMutation(name, items, true);
    },
    [commitCollection, sendCollectionMutation],
  );

  const handleRemoveCollectionItem = useCallback(
    (name, itemId) => {
      const items = (collectionsRef.current[name] || []).filter(
        (it) => it.id !== itemId,
      );
      commitCollection(name, items);
      sendCollectionMutation(name, items, true);
    },
    [commitCollection, sendCollectionMutation],
  );

  const handleCollectionFieldChange = useCallback(
    (name, itemId, fieldName, value) => {
      const items = applyCollectionFieldChange(
        collectionsRef.current[name],
        itemId,
        fieldName,
        value,
      );
      commitCollection(name, items);
      sendCollectionMutation(name, items, false);
    },
    [commitCollection, sendCollectionMutation],
  );

  const downloadAttachmentItem = useCallback(
    async (item) => {
      if (!item || !item.id) return false;
      const { data } = await downloadSingleDocument({
        variables: { where: { id: item.id } },
      });
      const payload = data && data.downloadSingleDocument;
      if (!payload) return false;
      const { document } = omitDeep(payload, "__typename");
      await downloadDocument(document);
      return true;
    },
    [downloadSingleDocument],
  );

  const downloadCollectionZip = useCallback(
    async (items, zipName) => {
      const variables = collectionDownloadQuery(items);
      if (!variables) return [];
      if (!shouldZipCollectionDownload(items)) {
        const downloaded = [];
        for (const item of items || []) {
          const ok = await downloadAttachmentItem(item);
          if (ok && item.id) downloaded.push(item.id);
        }
        return downloaded;
      }
      try {
        const { data } = await downloadDocuments({ variables });
        const payload = data && data.download;
        if (payload && payload.base64) {
          await downloadFiles(payload.base64, zipName);
          return items.map((item) => item && item.id).filter(Boolean);
        }
      } catch (_error) {
        // Fall through to one-by-one download when the zip mutation fails.
      }
      const downloaded = [];
      for (const item of items || []) {
        const ok = await downloadAttachmentItem(item);
        if (ok && item.id) downloaded.push(item.id);
      }
      return downloaded;
    },
    [downloadDocuments, downloadAttachmentItem],
  );

  const recordCollectionDownloads = useCallback((itemIds) => {
    const tracking = resolveDownloadTracking(slotsRef.current);
    if (!tracking) return;
    const pd = projectRef.current;
    if (!pd || !pd.id) return;
    if (trackedProjectIdRef.current !== pd.id) {
      trackedProjectIdRef.current = pd.id;
      downloadedIdsRef.current = parseDownloadedIds(
        pd.fields && pd.fields[tracking.downloadedIdsField],
      );
    }
    const nextIds = mergeDownloadedIds(downloadedIdsRef.current, itemIds);
    downloadedIdsRef.current = nextIds;
    const items = collectionsRef.current[tracking.collection] || [];
    const complete = areCollectionDownloadsComplete(items, nextIds);
    const write = handleFieldChangeRef.current;
    if (!write) return;
    write(
      {
        kind: "entity_field",
        entity: "advanced",
        field: tracking.downloadedIdsField,
      },
      JSON.stringify(nextIds),
    );
    write(
      {
        kind: "entity_field",
        entity: "advanced",
        field: tracking.downloadedField,
      },
      toBooleanFieldValue(complete),
    );
  }, []);

  const handleCollectionItemAction = useCallback(
    (collection, action, item, options = {}) => {
      const pd = projectRef.current;
      if (!pd || !pd.id) return;
      if (action === COLLECTION_ITEM_ACTIONS.DOWNLOAD) {
        downloadAttachmentItem(item)
          .then((ok) => {
            if (ok && item.id) recordCollectionDownloads([item.id]);
          })
          .catch(() => {});
        return;
      }
      if (PLAN_DOCUMENT_ACTIONS[action]) {
        if (!item || !item.id) return;
        downloadSingleDocument({
          variables: {
            where: {
              insurancePlans: { id: item.id },
              type: PLAN_DOCUMENT_ACTIONS[action],
            },
          },
          onCompleted: (payload) => {
            const { document } = omitDeep(
              payload.downloadSingleDocument,
              "__typename",
            );
            downloadDocument(document);
          },
        });
        return;
      }
      if (
        action === COLLECTION_ITEM_ACTIONS.SELECT_PLAN ||
        QUOTE_ITEM_ACTIONS.includes(action)
      ) {
        const writes = [
          updateEntity({
            variables: {
              project: { id: pd.id },
              entity: "advanced",
              field: "selectedPlan",
              value: item.id,
            },
          }),
        ];
        if (item.name) {
          writes.push(
            updateEntity({
              variables: {
                project: { id: pd.id },
                entity: "advanced",
                field: "selectedPlanName",
                value: item.name,
              },
            }),
          );
        }
        Promise.all(writes)
          .then(() => {
            if (onActionRef.current) {
              onActionRef.current({
                kind: SCREEN_BINDING_KINDS.PROCESS_TRIGGER,
                name: QUOTE_ITEM_ACTIONS.includes(action)
                  ? action
                  : PROCESS_TRIGGER_NAMES.NEXT,
                insurancePlanId: item.id,
                stay: options.stay === true,
              });
            }
          })
          .catch(() => {
            applyHopBusy(
              iframeRef.current && iframeRef.current.contentDocument,
              slotsRef.current,
              false,
            );
          });
        return;
      }
      if (onActionRef.current) {
        onActionRef.current({
          kind: "collection_action",
          action,
          collection,
          item,
        });
      }
    },
    [
      updateEntity,
      downloadSingleDocument,
      downloadAttachmentItem,
      recordCollectionDownloads,
    ],
  );

  onAddCollectionItemRef.current = handleAddCollectionItem;

  const flushPendingWrites = () => writeQueueRef.current.flush();

  const handleFieldChange = (binding, value) => {
    if (!binding || binding.kind !== "entity_field") return;

    if (binding.field === "searchQuery") {
      setCompanyQuery(String(value));
      return;
    }

    const pd = projectRef.current;
    if (!pd || !pd.id) return;

    const key = binding.entity + "." + binding.field;

    const group = (registryRef.current || []).find(
      (g) => g.key === binding.entity,
    );
    const fieldDefinition = group
      ? group.fields.find((f) => f.key === binding.field)
      : null;
    setPendingFields((prev) => ({
      ...prev,
      [storageKeyOf(binding, fieldDefinition)]: value,
    }));

    const write = () =>
      updateEntity({
        variables: {
          project: { id: pd.id },
          entity: binding.entity,
          field: binding.field,
          // A cleared field must reach the API as null, otherwise the previous
          // value stays stored while the screen shows an empty input.
          value: toEntityWriteValue(value),
        },
      });

    writeQueueRef.current.enqueue(key, write, 500);

    if (conditionalEvaluator.current) {
      conditionalEvaluator.current(
        binding.entity + "." + binding.field,
        String(value),
      );
    }
  };

  handleFieldChangeRef.current = handleFieldChange;

  onActionRef.current = async (binding) => {
    if (binding && binding.kind === "download_collection") {
      const items = resolveDownloadCollectionItems({
        binding,
        collections: collectionsRef.current,
        project: projectRef.current,
        registry: registryRef.current,
        statusAttachments,
      });
      if (!items.length) return;
      try {
        const downloaded = await downloadCollectionZip(
          items,
          collectionZipName(binding),
        );
        recordCollectionDownloads(downloaded);
      } catch (_error) {
        return;
      }
      return;
    }
    if (binding && binding.kind === "set_field") {
      handleFieldChange(
        { kind: "entity_field", entity: binding.entity, field: binding.field },
        binding.value,
      );
      return;
    }
    await flushPendingWrites();
    if (onAction) {
      await onAction(binding);
      return;
    }
    // Nothing will handle this trigger (the runner is processing), so undo the
    // hop feedback the click applied: no run update will come to clear it.
    applyHopBusy(
      iframeRef.current && iframeRef.current.contentDocument,
      slotsRef.current,
      false,
    );
  };

  useEffect(() => {
    if (!iframeRef.current || !extracted.html) return;
    const iframe = iframeRef.current;
    const doc = iframe.contentDocument;
    if (!doc) return;

    hasHydrated.current = false;
    setPortalAnchors({});

    const nextVersionId = version && version.id;
    const savedScroll = shouldKeepIframeScroll(
      lastVersionIdRef.current,
      nextVersionId,
    )
      ? readDocumentScroll(doc)
      : { top: 0, left: 0 };
    lastVersionIdRef.current = nextVersionId;

    doc.open();
    doc.write(buildFullHtml(extracted.html, version?.styleContent));
    doc.close();

    setupHydration({
      doc,
      refs: {
        slots: slotsRef,
        project: projectRef,
        registry: registryRef,
        onAction: onActionRef,
        handleFieldChange: handleFieldChangeRef,
        onAddCollectionItem: onAddCollectionItemRef,
        conditionalEvaluator,
        hydrate: hydrateRef,
        formDefinition: formDefinitionRef,
        dateDeltas: dateDeltasRef,
        validationMessages: validationMessagesRef,
      },
      handlers: { updateEntity, mountAttachmentField, fetchCities },
      debounceTimers,
    });

    const unbindExecuteField = bindExecuteFieldEvents(doc, {
      execute: (variables) => executeField({ variables }),
      getProjectId: () => get(projectRef.current, "id"),
      flush: flushPendingWrites,
    });

    const unbindAdvisorCode = bindAdvisorCodeEvents(doc, {
      resolve: (code) => {
        const currentProcessId = processIdRef.current;
        if (!currentProcessId || !code) return Promise.resolve(null);
        return resolveAdvisorCodeRef
          .current({
            variables: { processId: currentProcessId, code },
            fetchPolicy: "network-only",
          })
          .then((result) =>
            get(result, "data.resolveProcessAdvisorCode", null),
          );
      },
    });

    const onDomReady = () => {
      const anchors = {};
      const els = doc.querySelectorAll("[data-collection-portal]");
      for (const el of els) {
        anchors[el.getAttribute("data-collection-portal")] = el;
      }
      setPortalAnchors(anchors);
      if (hydrateRef.current) {
        if (!hasHydrated.current) {
          hasHydrated.current = true;
          for (const p of hydrateRef.current.slotProcessors) p.fn();
        }
        if (hydrateRef.current.evaluateConditionals) {
          hydrateRef.current.evaluateConditionals();
        }
      }
      if (progressPercent != null) {
        applyProgressBar(doc, progressPercent);
      }
      writeDocumentScroll(doc, savedScroll);
      requestAnimationFrame(() => {
        writeDocumentScroll(iframe.contentDocument, savedScroll);
      });
    };

    let observer;
    const tryReady = () => {
      if (doc.body) {
        onDomReady();
        if (observer) observer.disconnect();
        return true;
      }
      return false;
    };

    if (!tryReady()) {
      observer = new MutationObserver(() => {
        tryReady();
      });
      observer.observe(doc, { childList: true, subtree: true });
    }

    setHydrationKey((k) => k + 1);

    return () => {
      if (observer) observer.disconnect();
      if (unbindExecuteField) unbindExecuteField();
      if (unbindAdvisorCode) unbindAdvisorCode();
    };
    // Hydration is re-run only when the rendered document changes; the handlers
    // it wires up are read through refs so they never need to re-trigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extracted.html, version?.styleContent]);

  useEffect(() => {
    if (!hydrateRef.current || !hydrateRef.current.evaluateConditionals) return;
    hydrateRef.current.evaluateConditionals();
  }, [hydrationKey, project]);

  useEffect(() => {
    if (!hydrateRef.current || !hydrateRef.current.slotProcessors) return;
    for (const p of hydrateRef.current.slotProcessors) {
      if (p.refire) p.fn();
    }
  }, [
    hydrationKey,
    project,
    pendingFields,
    computedFields,
    factorPrices,
    dateDeltas,
    registry,
  ]);

  if (!version) return null;

  return (
    <div className="screen-renderer">
      <iframe
        ref={iframeRef}
        title="Screen"
        className="screen-renderer__iframe"
        sandbox={SCREEN_IFRAME_SANDBOX}
      />
      {Object.keys(portalAnchors).map((name) => {
        const anchor = portalAnchors[name];
        const tpl =
          extracted.collections[name] &&
          extracted.collections[name].itemTemplate;
        if (!anchor || !tpl) return null;
        const items = collections[name] || [];
        const portalKey = name + ":" + items.map((it) => it.id).join(",");
        const group = (registry || []).find((g) => g.key === name);
        const registryFields = group ? group.fields || [] : [];
        const selectedIdForCollection =
          name === "plans" ? selectedPlanId : null;
        return ReactDOM.createPortal(
          items.map((item) => {
            const perItem =
              computedFields && computedFields[item.id]
                ? computedFields[item.id]
                : null;
            const enrichedItem = perItem ? { ...item, ...perItem } : item;
            return (
              <CollectionItem
                key={item.id}
                template={tpl}
                item={enrichedItem}
                registryFields={registryFields}
                selected={
                  selectedIdForCollection
                    ? item.id === selectedIdForCollection
                    : false
                }
                onFieldChange={(fieldName, value) =>
                  handleCollectionFieldChange(name, item.id, fieldName, value)
                }
                onRemove={() => handleRemoveCollectionItem(name, item.id)}
                onAction={(action, it, options) =>
                  handleCollectionItemAction(name, action, it, options)
                }
              />
            );
          }),
          anchor,
          portalKey,
        );
      })}
    </div>
  );
};

export default ScreenRenderer;
